import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw, confirm_password: confirm });
      setDone(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e?.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
        <div className="w-full max-w-sm text-center space-y-4" data-testid="reset-missing-token">
          <h2 className="text-2xl tracking-tight font-semibold">Invalid reset link</h2>
          <p className="text-sm text-zinc-500">This link is missing its reset token. Request a new one below.</p>
          <Link to="/forgot-password" className="underline underline-offset-4 text-foreground text-sm">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
        <div className="w-full max-w-sm text-center space-y-5" data-testid="reset-success">
          <CheckCircle size={56} weight="duotone" className="text-emerald-500 mx-auto" />
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Your password has been reset successfully.</h2>
          <Button onClick={() => navigate("/login")} className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="reset-return-login-button">
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
      <form className="w-full max-w-sm space-y-5" onSubmit={submit} data-testid="reset-password-form">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Account recovery</div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Reset your password</h2>
        </div>

        {err && (
          <div className="text-sm text-red-600 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-sm" data-testid="reset-error">
            {err}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">New password</Label>
          <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Confirm new password</Label>
          <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="rounded-sm" />
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="reset-submit-button">
          {busy ? "Resetting…" : "Reset Password"}
        </Button>

        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="reset-back-to-login">
            Back to Login
          </Link>
        </p>
      </form>
    </div>
  );
}
